/** App-wide display locale: day/month/year (dd/mm/yyyy). */
export const DISPLAY_DATE_LOCALE = "en-GB";

/** Placeholder hint for date fields. */
export const DATE_INPUT_PLACEHOLDER = "dd/mm/yyyy";

/**
 * Parse API/ISO dates as local calendar days (avoids UTC off-by-one).
 * Also accepts dd/mm/yyyy.
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseLocalDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const str = String(value).trim();

  // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss(.sss)(Z|±hh:mm)
  const isoDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    const y = Number(isoDate[1]);
    const m = Number(isoDate[2]);
    const d = Number(isoDate[3]);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // DD/MM/YYYY
  const eu = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (eu) {
    const d = Number(eu[1]);
    const m = Number(eu[2]);
    const y = Number(eu[3]);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Convert a Date to YYYY-MM-DD for API payloads / controlled values.
 * @param {Date|null|undefined} date
 * @returns {string}
 */
export function toIsoDateString(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Format a date for display as DD/MM/YYYY (always European).
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatDisplayDate(value) {
  if (value == null || value === "") return "";
  const d = parseLocalDate(value);
  if (!d) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Format a date-time for display as DD/MM/YYYY, HH:mm (24h).
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatDisplayDateTime(value) {
  if (value == null || value === "") return "";

  let d;
  if (value instanceof Date) {
    d = value;
  } else {
    const str = String(value).trim();
    // Date-only → local midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      d = parseLocalDate(str);
    } else {
      d = new Date(str);
    }
  }

  if (!d || Number.isNaN(d.getTime())) return String(value);

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}, ${hh}:${min}`;
}
